import React, {useEffect, useState} from 'react';
import superAdminAPI from '../services/superAdminAPI';
import {AlertTriangle, Loader, X} from 'lucide-react';

const SchoolEditModal = ({isOpen, onClose, onSchoolUpdated, school}) => {
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        subscription_plan: 'trial',
        max_users: 50,
        address: {
            city: '',
            state: '',
            country: '',
            postal_code: '',
            street_address: ''
        },
        contact_info: {
            email: '',
            phone: '',
            contact_person: ''
        }
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);

    // Load school data when modal opens or school changes
    useEffect(() => {
        if (school && isOpen) {
            setFormData({
                name: school.name || '',
                domain: school.domain || '',
                subscription_plan: school.subscription_plan || 'trial',
                max_users: school.max_users || 50,
                address: {
                    city: school.address?.city || '',
                    state: school.address?.state || '',
                    country: school.address?.country || '',
                    postal_code: school.address?.postal_code || '',
                    street_address: school.address?.street_address || ''
                },
                contact_info: {
                    email: school.contact_info?.email || '',
                    phone: school.contact_info?.phone || '',
                    contact_person: school.contact_info?.contact_person || ''
                }
            });
        }
    }, [school, isOpen]);

    const handleChange = (e) => {
        const {name, value} = e.target;

        if (name.includes('.')) {
            const [section, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({...prev, [name]: value}));
        }
    };

    const validateStep1 = () => {
        if (!formData.name) return "School name is required";
        if (!formData.domain) return "Domain is required";
        return null;
    };

    const validateStep2 = () => {
        if (!formData.contact_info.email) return "Contact email is required";
        if (!formData.contact_info.contact_person) return "Contact person is required";
        return null;
    };

    const nextStep = () => {
        let error = null;

        if (step === 1) {
            error = validateStep1();
        } else if (step === 2) {
            error = validateStep2();
        }

        if (error) {
            setError(error);
            return;
        }

        setError(null);
        setStep(step + 1);
    };

    const prevStep = () => {
        setError(null);
        setStep(step - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateStep2();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await superAdminAPI.updateSchool(school.id, formData);
            onSchoolUpdated(response);
            setStep(1);
        } catch (error) {
            console.error("Error updating school:", error);
            setError(superAdminAPI.handleError(error));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-800">Edit School</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        <X size={20}/>
                    </button>
                </div>

                <div className="p-6">
                    {/* Progress Indicator */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                            {[1, 2].map((s) => (
                                <div
                                    key={s}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                                        ${s < step ? 'bg-green-500 text-white' :
                                        s === step ? 'bg-orange-500 text-white' :
                                            'bg-gray-200 text-gray-600'}`}
                                >
                                    {s}
                                </div>
                            ))}
                        </div>
                        <div className="relative">
                            <div className="absolute top-0 left-0 h-1 bg-gray-200 w-full"></div>
                            <div
                                className="absolute top-0 left-0 h-1 bg-orange-500"
                                style={{width: `${(step - 1) * 100}%`}}
                            ></div>
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-gray-600">
                            <span>School Details</span>
                            <span>Contact Information</span>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div
                            className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm flex items-start gap-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5"/>
                            <div>{error}</div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Step 1: School Details */}
                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        School Name*
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., ABC International School"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Domain Prefix*
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="text"
                                            name="domain"
                                            value={formData.domain}
                                            onChange={handleChange}
                                            className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            placeholder="e.g., abcschool"
                                        />
                                        <span
                                            className="bg-gray-100 p-3 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                                            .edumonics.com
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        This will be used as the subdomain for school access
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Subscription Plan
                                    </label>
                                    <select
                                        name="subscription_plan"
                                        value={formData.subscription_plan}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    >
                                        <option value="trial">Trial</option>
                                        <option value="basic">Basic</option>
                                        <option value="premium">Premium</option>
                                        <option value="enterprise">Enterprise</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Maximum Users
                                    </label>
                                    <input
                                        type="number"
                                        name="max_users"
                                        value={formData.max_users}
                                        onChange={handleChange}
                                        min="1"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Step 2: Contact Information */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Person*
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_info.contact_person"
                                        value={formData.contact_info.contact_person}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., John Smith"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Email*
                                    </label>
                                    <input
                                        type="email"
                                        name="contact_info.email"
                                        value={formData.contact_info.email}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., contact@school.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Contact Phone
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_info.phone"
                                        value={formData.contact_info.phone}
                                        onChange={handleChange}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        placeholder="e.g., +1234567890"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            name="address.city"
                                            value={formData.address.city}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State/Province
                                        </label>
                                        <input
                                            type="text"
                                            name="address.state"
                                            value={formData.address.state}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            name="address.country"
                                            value={formData.address.country}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Postal Code
                                        </label>
                                        <input
                                            type="text"
                                            name="address.postal_code"
                                            value={formData.address.postal_code}
                                            onChange={handleChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex justify-between">
                            {step > 1 ? (
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Back
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}

                            {step < 2 ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader size={16} className="animate-spin"/>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update School'
                                    )}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SchoolEditModal;